import React, { ReactNode, useEffect, useState } from "react"
import {
  Box,
  Button as ChakraButton,
  Divider,
  Flex,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { HexColorPicker } from "react-colorful"
import Icon from "components/elements/Icon"
import Button from "components/Buttons/Button"
import { colorIsValid, colorWithHash } from "utilities/colors"
import {
  COLOR_THEMES, CUSTOM_THEME_NAME, PORTAL_COLOR_FIELDS, PortalColors, themeNameFor,
} from "./themes"

type Props = {
  colors: PortalColors
  onChange: (colors: PortalColors) => void
  hasChanges: boolean
  onSave: () => Promise<void>
  onCancel: () => void
}

type View = "themes" | "customize"

const ColorThemePicker = ({
  colors, onChange, hasChanges, onSave, onCancel,
}: Props) => {
  const [view, setView] = useState<View>("themes")
  const [openField, setOpenField] = useState<keyof PortalColors | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const themeName = themeNameFor(colors)
  const isCustom = themeName === CUSTOM_THEME_NAME

  const reset = () => {
    setView("themes")
    setOpenField(null)
  }

  const open = () => {
    reset()
    onOpen()
  }

  const cancel = () => {
    onCancel()
    onClose()
  }

  const save = () => {
    setIsSaving(true)
    onSave()
      .then(onClose)
      .catch(() => {})
      .finally(() => setIsSaving(false))
  }

  return (
    <HStack spacing={3}>
      <Popover placement="bottom-end" isOpen={isOpen} onOpen={open} onClose={onClose} isLazy>
        <PopoverTrigger>
          <ChakraButton
            variant="ghost"
            size="sm"
            color="black"
            aria-label={hasChanges ? `Color Theme: ${themeName}, unsaved` : `Color Theme: ${themeName}`}
          >
            <HStack spacing={2}>
              <Box position="relative">
                <Swatches colors={colors} />
                {hasChanges && (
                  <Box
                    position="absolute"
                    top="-3px"
                    right="-3px"
                    boxSize={2}
                    borderRadius="full"
                    bg="orange.400"
                    borderWidth={1}
                    borderColor="white"
                  />
                )}
              </Box>
              <Icon icon="down-arrow" size={3} color="currentColor" />
            </HStack>
          </ChakraButton>
        </PopoverTrigger>
        <Portal>
          <PopoverContent w="320px">
            <PopoverArrow bg={view === "customize" ? "gray.50" : "white"} />
            {view === "customize" && (
              <Header>
                <Flex
                  as="button"
                  type="button"
                  align="center"
                  justify="center"
                  w={6}
                  color="blue.500"
                  onClick={reset}
                  aria-label="Back"
                >
                  <Icon icon="prev-arrow" />
                </Flex>
                <Box flex={1} textAlign="center" fontSize="md">Customize</Box>
                <Box w={6} />
              </Header>
            )}
            <PopoverBody p={0} py={1}>
              {view === "themes" ? (
                <>
                  {COLOR_THEMES.map(({ name, colors: themeColors }) => (
                    <Row
                      key={name}
                      selected={name === themeName}
                      onClick={() => onChange(themeColors)}
                    >
                      <Text flex={1}>{name}</Text>
                      <Swatches colors={themeColors} />
                    </Row>
                  ))}
                  <Divider my={1} />
                  <Row selected={isCustom} onClick={() => setView("customize")}>
                    <Text flex={1}>Customize</Text>
                    <Icon icon="next-arrow" size={3} color="gray.500" />
                  </Row>
                </>
              ) : PORTAL_COLOR_FIELDS.map(({ key, label }) => (
                <ColorField
                  key={key}
                  label={label}
                  color={colors[key]}
                  isOpen={openField === key}
                  onToggle={() => setOpenField(openField === key ? null : key)}
                  onChange={(color) => onChange({ ...colors, [key]: color })}
                />
              ))}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
      <HStack spacing={2}>
        <Button size="sm" colorScheme="button.light" onClick={cancel} isDisabled={!hasChanges || isSaving}>
          Cancel
        </Button>
        <Button size="sm" onClick={save} isLoading={isSaving} isDisabled={!hasChanges}>
          Save
        </Button>
      </HStack>
    </HStack>
  )
}

const Header = ({ children }: { children: ReactNode }) => (
  <Flex
    minH={14}
    px={5}
    py={3}
    bg="gray.50"
    borderTopRadius="md"
    borderBottomWidth={1}
    borderColor="gray.200"
    align="center"
  >
    {children}
  </Flex>
)

type RowProps = {
  selected?: boolean
  checkSlot?: boolean
  onClick: () => void
  children: ReactNode
}

const Row = ({
  selected, checkSlot = true, onClick, children,
}: RowProps) => (
  <Flex
    as="button"
    type="button"
    w="full"
    align="center"
    gap={2}
    px={3}
    py={2}
    textAlign="left"
    aria-pressed={checkSlot ? Boolean(selected) : undefined}
    bg={selected ? "blue.50" : "transparent"}
    _hover={{ bg: selected ? "blue.50" : "gray.100" }}
    onClick={onClick}
  >
    {checkSlot && (
      <Box w={4} flexShrink={0}>
        {selected && <Icon icon="checkmark" size={3.5} color="blue.500" />}
      </Box>
    )}
    {children}
  </Flex>
)

type ColorFieldProps = {
  label: string
  color: string
  isOpen: boolean
  onToggle: () => void
  onChange: (color: string) => void
}

const ColorField = ({
  label, color, isOpen, onToggle, onChange,
}: ColorFieldProps) => {
  const [text, setText] = useState(color)

  useEffect(() => setText(color), [color])

  const handleTextChange = (value: string) => {
    const hex = colorWithHash(value)
    setText(hex)
    if (colorIsValid(hex)) {
      onChange(hex)
    }
  }

  return (
    <Box>
      <Row checkSlot={false} onClick={onToggle}>
        <Box boxSize={5} flexShrink={0} bg={color} borderWidth={1} borderColor="gray.300" borderRadius="sm" />
        <Text flex={1}>{label}</Text>
        <Text fontSize="sm" color="gray.600">{color.toUpperCase()}</Text>
      </Row>
      {isOpen && (
        <Box px={3} pt={1} pb={3} sx={{ ".react-colorful": { w: "full", h: "160px" } }}>
          <HexColorPicker color={color} onChange={onChange} />
          <Input
            mt={2}
            size="sm"
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            aria-label={`${label} hex`}
          />
        </Box>
      )}
    </Box>
  )
}

const Swatches = ({ colors }: { colors: PortalColors }) => (
  <HStack spacing={1}>
    {PORTAL_COLOR_FIELDS.map(({ key }) => (
      <Box key={key} boxSize={4} bg={colors[key]} borderWidth={1} borderColor="gray.300" borderRadius="sm" />
    ))}
  </HStack>
)

export default ColorThemePicker
